import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Modal from "react-modal";

import { getSubscriptionInfo, addFormulaOrder } from "../../api";
import { TTP_PAYMENT_URL } from "../../config";
import Button from "../common/Button";
import PeriodChoice from "./PeriodChoice";
import styles from "./SubscriptionModal.module.scss";

const SubscriptionModal = ({
  isOpen,
  closeModal,
  fiduciary,
  currentPan,
  newPlan,
  token,
  onSuccessMonthlyUpgrade,
}) => {
  const language = useSelector((state) => state.params.language);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 | 2
  const [period, setPeriod] = useState("MONTHLY"); // MONTHLY | YEARLY
  const [billingInfos, setBillingInfos] = useState(null);
  const [order, setOrder] = useState(null);

  const [checkedSendByEmail, setCheckedSendByEmail] = useState(true);

  useEffect(() => {
    if (currentPan?.monthPrice > 0) {
      setPeriod(currentPan.period);
    }
  }, [currentPan]);

  const handleCloseModal = () => {
    setStep(1);
    closeModal();
  };

  useEffect(() => {
    const handler = (e) => {
      const eventId = e?.data?.event;
      const paymentIframe = document.getElementById("modal-payment-iframe");

      if (e?.data?.message === "CLOSE_PAYMENT_MODAL") {
        handleCloseModal();
      }

      if (e?.data?.event === "RETRY_PAYMENT") {
        paymentIframe.src += ""; // To refresh the iframe
      }
    };

    window.addEventListener("message", handler);

    return () => {
      window.removeEventListener("message", handler);
    };
  }, []);

  useEffect(() => {
    if (isOpen && newPlan) {
      setIsLoading(true);
      getSubscriptionInfo(
        token,
        fiduciary.id,
        currentPan ? currentPan.id : null,
        newPlan.id
      )
        .then((response) => {
          setBillingInfos(response.data.data);
        })
        .catch((e) => {})
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen]);

  const handleCreateOrder = () => {
    let orderData = {
      token,
      organizationId: fiduciary.id,
      language,
      period,
      formulaId: newPlan.id,
      uen: billingInfos.addresses[0].billingCompanyNumber,
      organization: billingInfos.addresses[0].billingOrganization,
      address: billingInfos.addresses[0].billingStreet,
      postalCode: billingInfos.addresses[0].billingPostalCode,
      city: billingInfos.addresses[0].billingCountry,
      subjectToVAT: billingInfos.addresses[0].billingSubjectToVAT,
      doNotSendInvoice: checkedSendByEmail === false,
    };
    if (currentPan.monthPrice > 0) {
      orderData.upgradeFrom = currentPan.id;
    }

    setIsLoading(true);
    addFormulaOrder(orderData)
      .then((response) => {
        if (response.data.data) {
          if (currentPan?.monthPrice > 0 && currentPan?.period === "MONTHLY") {
            if (onSuccessMonthlyUpgrade) {
              setIsLoading(false);
              handleCloseModal();
              onSuccessMonthlyUpgrade();
            }
          } else {
            setOrder(response.data.data);
            setStep(2);
            setTimeout(() => setIsLoading(false), 800);
          }
        }
      })
      .catch((e) => {
        setIsLoading(false);
        toast.error(_("error_add_order"));
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleCloseModal}
      shouldCloseOnOverlayClick={false}
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      {isLoading && <div className="lmask" />}
      <div className={styles.modalHeader}>
        Abonnement Site Builder – Plan {newPlan?.nameFr}
        <span className={styles.modalClose} onClick={handleCloseModal}>
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="20"
              cy="20"
              r="19.75"
              stroke="white"
              stroke-width="0.5"
            />
            <path
              d="M18.8579 20.0005L18.5044 19.6469L12.9582 14.1008C12.6429 13.7855 12.643 13.2744 12.9582 12.9592L12.9582 12.9592C13.2735 12.6439 13.7846 12.6439 14.0998 12.9592L19.646 18.5054L19.9996 18.8589L20.3531 18.5054L25.8993 12.9591C25.8993 12.9591 25.8993 12.9591 25.8993 12.9591C26.2146 12.6438 26.7257 12.6438 27.0409 12.9591C27.0409 12.9591 27.0409 12.9591 27.0409 12.9591M18.8579 20.0005L27.0409 12.9591M18.8579 20.0005L18.5044 20.354M18.8579 20.0005L18.5044 20.354M27.0409 12.9591C27.3562 13.2744 27.3561 13.7855 27.041 14.1007M27.0409 12.9591L27.041 14.1007M27.041 14.1007C27.041 14.1007 27.0409 14.1007 27.0409 14.1007M27.041 14.1007L27.0409 14.1007M27.0409 14.1007L21.4947 19.6468M27.0409 14.1007L21.4947 19.6468M18.5044 20.354L12.9582 25.9002C12.643 26.2154 12.643 26.7265 12.9582 27.0418C13.2735 27.357 13.7846 27.357 14.0998 27.0418L14.0998 27.0418L19.646 21.4956L19.9996 21.142L20.3531 21.4956L25.8993 27.0418C26.2146 27.357 26.7257 27.357 27.0409 27.0418C27.3561 26.7266 27.3562 26.2154 27.0409 25.9002C27.0409 25.9002 27.0409 25.9001 27.0409 25.9001L21.4947 20.354L21.1412 20.0004M18.5044 20.354L21.1412 20.0004M21.1412 20.0004L21.4947 19.6468M21.1412 20.0004L21.4947 19.6468"
              fill="white"
              stroke="white"
            />
          </svg>
        </span>
      </div>

      {step === 1 && billingInfos && (
        <>
          <PeriodChoice
            billingInfos={
              billingInfos?.addresses?.length > 0
                ? billingInfos.addresses[0]
                : null
            }
            period={period}
            setPeriod={setPeriod}
            plan={newPlan}
            currentPan={currentPan}
            nextYear={billingInfos?.nextYear}
            nextMonth={billingInfos?.nextMonth}
            yearRemainPrice={billingInfos?.yearRemainPrice}
          />

          <div className={styles.period_actions}>
            <Button onClick={handleCreateOrder}>
              {currentPan?.monthPrice > 0 &&
              currentPan.period === "MONTHLY" &&
              newPlan
                ? "Je confirme l'abonnement au plan " + newPlan?.nameFr
                : "Faire le paiement"}
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <div className={styles.wrapper}>
          <iframe
            id="modal-payment-iframe"
            className={styles.iframe}
            src={`${TTP_PAYMENT_URL}/paymentMethode?orderID=${order.id}&token=${token}&embedded=1`}
            title="Payment"
          />
        </div>
      )}
    </Modal>
  );
};
export default SubscriptionModal;
